import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const cache = new Map();

async function fetchAudio(text) {
  if (cache.has(text)) return cache.get(text);
  const { data } = await api.post("/tts", { text }, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  cache.set(text, url);
  return url;
}

export function wordWeights(words) {
  const weights = words.map((w) => w.length + 2);
  const total = weights.reduce((a, b) => a + b, 0);
  let acc = 0;
  return weights.map((w) => (acc += w) / total);
}

export function useNarration() {
  const audioRef = useRef(null);
  const [state, setState] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [rate, setRate] = useState(1);
  const [activeText, setActiveText] = useState(null);

  const tokenRef = useRef(0);

  const stop = useCallback(() => {
    tokenRef.current += 1;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setState("idle");
    setProgress(0);
    setActiveText(null);
  }, []);

  useEffect(() => stop, [stop]);
  useEffect(() => { if (audioRef.current) audioRef.current.playbackRate = rate; }, [rate]);

  const play = useCallback(async (text) => {
    if (audioRef.current && activeText === text) {
      if (audioRef.current.paused) { audioRef.current.play(); setState("playing"); }
      else { audioRef.current.pause(); setState("paused"); }
      return;
    }
    stop();
    const token = tokenRef.current;
    setActiveText(text);
    setState("loading");
    try {
      const url = await fetchAudio(text);
      if (token !== tokenRef.current) return;
      const audio = new Audio(url);
      audio.playbackRate = rate;
      audioRef.current = audio;
      audio.ontimeupdate = () => audio.duration && setProgress(audio.currentTime / audio.duration);
      audio.onended = () => { setState("idle"); setProgress(1); };
      await audio.play();
      setState("playing");
    } catch (e) {
      if (token !== tokenRef.current) return;
      setState("idle");
      setActiveText(null);
      if (e?.response?.status === 402) {
        toast.info("La lecture à voix haute fait partie de Lis-Moi Premium.", {
          action: { label: "Découvrir", onClick: () => window.location.assign("/premium") },
        });
        return;
      }
      toast.error("La narration n'a pas pu démarrer. Réessaie dans un instant.");
    }
  }, [activeText, rate, stop]);

  return { play, stop, state, progress, rate, setRate, activeText };
}
